<template>
  <div class="content-page report-page">
    <div class="report-container">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h4 class="mb-0 font-weight-bold">
          <font-awesome-icon class="mr-2 color-primary" icon="stopwatch" />
          ความเร็วการส่งข้อความ WhatsApp
        </h4>
        <b-button variant="light" @click="goToChat">
          <font-awesome-icon class="mr-1" icon="chevron-left" />กลับไปหน้าแชท
        </b-button>
      </div>
      <p class="text-muted small mb-3">
        ดูว่าข้อความที่ส่งหาลูกค้าทาง WhatsApp
        ใช้เวลานานแค่ไหนกว่าจะถึงมือถือลูกค้า แยกตามวันที่ส่ง
        ข้อความที่ใช้เวลานานมาก
        มักเป็นเพราะมือถือลูกค้าปิดอยู่หรือไม่มีอินเทอร์เน็ต ไม่ใช่เพราะระบบช้า
      </p>

      <!-- filter -->
      <b-row class="align-items-end">
        <b-col cols="6" md="3" xl="2">
          <b-form-group label="วันที่เริ่มต้น" label-for="report-start-date">
            <datetime
              input-id="report-start-date"
              placeholder="วัน/เดือน/ปี"
              v-model="filter.from"
              format="dd/MM/yyyy"
              class="theme-pink"
              input-class="form-control"
            ></datetime>
          </b-form-group>
        </b-col>
        <b-col cols="6" md="3" xl="2">
          <b-form-group label="วันที่สิ้นสุด" label-for="report-end-date">
            <datetime
              input-id="report-end-date"
              placeholder="วัน/เดือน/ปี"
              v-model="filter.to"
              format="dd/MM/yyyy"
              class="theme-pink"
              input-class="form-control"
            ></datetime>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2" xl="1">
          <b-form-group>
            <b-button
              variant="primary"
              class="w-100 bg-color-primary"
              :disabled="isBusy"
              @click="getReport"
              >ค้นหา</b-button
            >
          </b-form-group>
        </b-col>
      </b-row>
      <div v-if="errorMessage" class="text-danger small mb-3">
        <font-awesome-icon class="mr-1" icon="exclamation-circle" />{{
          errorMessage
        }}
      </div>

      <!-- summary -->
      <b-row class="mb-3">
        <b-col
          v-for="tile in summaryTiles"
          :key="tile.label"
          cols="6"
          lg="3"
          class="mb-2"
        >
          <div class="stat-tile">
            <div class="stat-label">{{ tile.label }}</div>
            <div class="stat-value">{{ tile.value }}</div>
            <div class="stat-note">{{ tile.note }}</div>
          </div>
        </b-col>
      </b-row>

      <!-- distribution -->
      <div class="report-card mb-3">
        <div class="font-weight-bold mb-1">
          ข้อความถึงมือถือลูกค้าเร็วแค่ไหน (แยกรายวัน)
        </div>
        <div class="text-muted small mb-2">
          ยิ่งแถบสีอ่อนยาว ยิ่งดี ตัวเลขด้านขวาคือจำนวนข้อความที่ถึงลูกค้าแล้ว
        </div>
        <div class="d-flex flex-wrap mb-2 legend">
          <div
            v-for="bucket in buckets"
            :key="bucket.key"
            class="d-flex align-items-center mr-3"
          >
            <span
              class="legend-swatch"
              :style="{ backgroundColor: bucket.color }"
            ></span>
            {{ bucket.label }}
          </div>
        </div>
        <div v-if="isBusy" class="text-center my-3">
          <b-spinner class="align-middle"></b-spinner>
        </div>
        <div v-else-if="!deliveredRows.length" class="text-muted my-3">
          ไม่พบข้อมูลในช่วงวันที่ที่เลือก
        </div>
        <div v-else>
          <div
            v-for="row in deliveredRows"
            :key="row.date"
            class="d-flex align-items-center bucket-row"
          >
            <div class="bucket-date">{{ row.date | moment("DD MMM") }}</div>
            <div class="bucket-bar">
              <div
                v-for="bucket in buckets.filter(b => row[b.key] > 0)"
                :key="bucket.key"
                v-b-tooltip.hover
                :title="bucketTooltip(row, bucket)"
                class="bucket-segment"
                :style="{
                  flexGrow: row[bucket.key],
                  backgroundColor: bucket.color
                }"
              ></div>
            </div>
            <div class="bucket-count">
              {{ row.deliveredCount | numeral("0,0") }}
            </div>
          </div>
        </div>
      </div>

      <!-- table -->
      <div class="report-card">
        <b-table
          responsive
          hover
          small
          :busy="isBusy"
          :items="items"
          :fields="fields"
          show-empty
          empty-text="ไม่พบข้อมูลในช่วงวันที่ที่เลือก"
          class="mb-0"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>
          <template #cell(date)="{ item }">
            {{ item.date | moment($formatDate) }}
          </template>
          <template #cell(metaSentMedianSeconds)="{ item }">
            {{ formatDuration(item.metaSentMedianSeconds, item.metaSentCount) }}
          </template>
          <template #cell(deliveredMedianSeconds)="{ item }">
            {{
              formatDuration(item.deliveredMedianSeconds, item.deliveredCount)
            }}
          </template>
          <template #cell(deliveredP90Seconds)="{ item }">
            {{ formatDuration(item.deliveredP90Seconds, item.deliveredCount) }}
          </template>
          <template #cell(deliveredMaxSeconds)="{ item }">
            {{ formatDuration(item.deliveredMaxSeconds, item.deliveredCount) }}
          </template>
          <template #cell(deliveredCount)="{ item }">
            {{ item.deliveredCount | numeral("0,0") }}
          </template>
          <template #cell(pendingCount)="{ item }">
            {{ item.pendingCount | numeral("0,0") }}
          </template>
          <template #cell(failedCount)="{ item }">
            <span :class="{ 'text-danger': item.failedCount > 0 }">
              {{ (item.failedCount || 0) | numeral("0,0") }}
            </span>
          </template>
        </b-table>
        <ul class="text-muted small mt-3 mb-0 pl-3">
          <li>
            <b>ส่วนใหญ่ถึงภายใน</b> =
            ครึ่งหนึ่งของข้อความถึงมือถือลูกค้าภายในเวลานี้ ใช้ดูความเร็วตามปกติ
          </li>
          <li>
            <b>9 ใน 10 ข้อความถึงภายใน</b> = มีเพียง 1 ใน 10
            ข้อความที่ใช้เวลานานกว่านี้
          </li>
          <li>
            <b>ระบบส่งออกภายใน</b> = เวลาที่ระบบเราใช้ส่งข้อความออกไปถึง
            WhatsApp ถ้าค่านี้สูงแปลว่าระบบช้า
          </li>
          <li>
            <b>ยังไม่ถึงลูกค้า</b> = ส่งออกไปแล้วแต่มือถือลูกค้ายังไม่ได้รับ
            (มือถือปิดหรือไม่มีอินเทอร์เน็ต)
          </li>
          <li>
            <b>ส่งไม่สำเร็จ</b> = WhatsApp แจ้งว่าส่งข้อความนี้ไม่ได้
            ดูรายชื่อลูกค้าได้ในตารางด้านล่าง
          </li>
        </ul>
      </div>

      <!-- failed messages -->
      <div class="report-card mt-3">
        <div class="font-weight-bold mb-1">
          <font-awesome-icon
            class="mr-1 text-danger"
            icon="exclamation-circle"
          />
          ข้อความที่ส่งไม่สำเร็จ ({{ failedMessages.length | numeral("0,0") }})
        </div>
        <div class="text-muted small mb-2">
          ข้อความที่ส่งไม่ถึงลูกค้า พร้อมชื่อลูกค้าและสาเหตุ
        </div>
        <b-table
          responsive
          hover
          small
          :busy="isBusy"
          :items="failedMessages"
          :fields="failedFields"
          :per-page="failedPerPage"
          :current-page="failedPage"
          show-empty
          empty-text="ไม่มีข้อความที่ส่งไม่สำเร็จในช่วงวันที่ที่เลือก"
          class="mb-0"
        >
          <template #table-busy>
            <div class="text-center my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>
          <template #cell(sentTime)="{ item }">
            {{ item.sentTime | moment("DD/MM/YYYY HH:mm") }}
          </template>
          <template #cell(displayName)="{ item }">
            {{ item.displayName || "-" }}
          </template>
          <template #cell(psid)="{ item }">
            {{ item.psid || "-" }}
          </template>
          <template #cell(reason)="{ item }">
            <div>{{ failedReason(item) }}</div>
            <div class="text-muted small">
              code {{ item.errorCode || "-" }}
              <span v-if="item.errorTitle">· {{ item.errorTitle }}</span>
            </div>
          </template>
        </b-table>
        <b-pagination
          v-if="failedMessages.length > failedPerPage"
          v-model="failedPage"
          :total-rows="failedMessages.length"
          :per-page="failedPerPage"
          size="sm"
          align="right"
          class="mt-2 mb-0"
        ></b-pagination>
      </div>
    </div>
    <ModalPanelGloabal />
  </div>
</template>

<script>
import * as moment from "moment/moment";
import { Datetime } from "vue-datetime";
import { mapGetters } from "vuex";
import ModalPanelGloabal from "@/components/modal/alert/ModalPanelGloabal";
import {
  canViewWhatsappReport,
  getWhatsappDelivery,
  formatDuration
} from "@/helpers/whatsappDelivery";

// backend จำกัดช่วงไม่เกิน 93 วัน
const MAX_RANGE_DAYS = 92;

// error code ที่เจอบ่อยจาก WhatsApp → คำอธิบายภาษาไทย (ไม่มีในนี้ใช้ errorTitle จาก Meta)
const FAILED_REASONS = {
  131047: "เกิน 24 ชม. นับจากข้อความล่าสุดของลูกค้า",
  131042: "บัญชี WhatsApp Business มีปัญหาการชำระเงิน",
  131026: "ส่งไม่ถึงเบอร์นี้ (ไม่ได้ใช้ WhatsApp หรือแอปเก่าเกินไป)",
  131049: "WhatsApp จำกัดการส่งข้อความหาลูกค้ารายนี้",
  131051: "ประเภทข้อความไม่รองรับ",
  131053: "อัปโหลดไฟล์แนบไม่สำเร็จ",
  131056: "ส่งหาลูกค้ารายนี้ถี่เกินไป",
  130429: "ส่งเกินจำนวนที่ WhatsApp กำหนด",
  131000: "เกิดข้อผิดพลาดที่ WhatsApp"
};

export default {
  name: "whatsapp-delivery-report",
  components: {
    datetime: Datetime,
    ModalPanelGloabal
  },
  data() {
    return {
      isBusy: false,
      errorMessage: "",
      items: [],
      filter: {
        from: moment()
          .subtract(6, "days")
          .format(),
        to: moment().format()
      },
      // ช่วงเวลาเรียงจากเร็ว → ช้า ใช้สีฟ้าโทนเดียวไล่อ่อน → เข้ม
      buckets: [
        { key: "delivered0To10s", label: "ภายใน 10 วินาที", color: "#86b6ef" },
        {
          key: "delivered10sTo1m",
          label: "10 วินาที – 1 นาที",
          color: "#3987e5"
        },
        {
          key: "delivered1mTo1h",
          label: "1 นาที – 1 ชั่วโมง",
          color: "#1c5cab"
        },
        { key: "deliveredOver1h", label: "นานกว่า 1 ชั่วโมง", color: "#0d366b" }
      ],
      fields: [
        { key: "date", label: "วันที่" },
        {
          key: "deliveredCount",
          label: "ถึงลูกค้าแล้ว",
          class: "text-right"
        },
        { key: "pendingCount", label: "ยังไม่ถึงลูกค้า", class: "text-right" },
        { key: "failedCount", label: "ส่งไม่สำเร็จ", class: "text-right" },
        {
          key: "deliveredMedianSeconds",
          label: "ส่วนใหญ่ถึงภายใน",
          class: "text-right"
        },
        {
          key: "deliveredP90Seconds",
          label: "9 ใน 10 ข้อความถึงภายใน",
          class: "text-right"
        },
        {
          key: "deliveredMaxSeconds",
          label: "นานที่สุด",
          class: "text-right"
        },
        {
          key: "metaSentMedianSeconds",
          label: "ระบบส่งออกภายใน",
          class: "text-right"
        }
      ],
      failedPage: 1,
      failedPerPage: 20,
      failedFields: [
        { key: "sentTime", label: "เวลาที่ส่ง" },
        { key: "displayName", label: "ลูกค้า" },
        { key: "psid", label: "เบอร์ WhatsApp" },
        { key: "reason", label: "สาเหตุ" }
      ]
    };
  },
  computed: {
    ...mapGetters({
      brandID: "getBrand",
      adminProfile: "getChatAdminProfile"
    }),
    deliveredRows() {
      return this.items.filter(x => x.deliveredCount > 0);
    },
    // รวมทุกวัน ล่าสุดขึ้นก่อน
    failedMessages() {
      return this.items
        .reduce((all, x) => all.concat(x.failedMessages || []), [])
        .sort((a, b) => moment(b.sentTime).diff(moment(a.sentTime)));
    },
    summaryTiles() {
      const sum = key => this.items.reduce((total, x) => total + x[key], 0);
      const delivered = sum("deliveredCount");
      const within10s = sum("delivered0To10s");
      const over1h = sum("deliveredOver1h");
      const percent = value =>
        delivered ? `${((value / delivered) * 100).toFixed(1)}%` : "-";
      return [
        {
          label: "ข้อความที่ส่งออกไป",
          value: this.formatNumber(sum("metaSentCount")),
          note: "ในช่วงวันที่ที่เลือก"
        },
        {
          label: "ถึงมือถือลูกค้าแล้ว",
          value: this.formatNumber(delivered),
          note: `ยังไม่ถึงอีก ${this.formatNumber(sum("pendingCount"))} ข้อความ`
        },
        {
          label: "ถึงเร็ว (ภายใน 10 วินาที)",
          value: percent(within10s),
          note: `${this.formatNumber(within10s)} ข้อความ`
        },
        {
          label: "ถึงช้า (นานกว่า 1 ชั่วโมง)",
          value: percent(over1h),
          note: "มักเพราะมือถือลูกค้าปิดอยู่"
        }
      ];
    }
  },
  methods: {
    goToChat() {
      this.$router.push(`/chat?brandID=${this.brandID || 1}`);
    },
    formatDuration,
    failedReason(item) {
      return (
        FAILED_REASONS[item.errorCode] || item.errorTitle || "ไม่ทราบสาเหตุ"
      );
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-US");
    },
    bucketTooltip(row, bucket) {
      const count = row[bucket.key];
      const percent = ((count / row.deliveredCount) * 100).toFixed(1);
      return `${bucket.label}: ${this.formatNumber(
        count
      )} ข้อความ (${percent}%)`;
    },
    validateFilter() {
      if (!this.filter.from || !this.filter.to)
        return "กรุณาเลือกวันที่เริ่มต้นและวันที่สิ้นสุด";
      const from = moment(this.filter.from).startOf("day");
      const to = moment(this.filter.to).startOf("day");
      if (from.isAfter(to)) return "วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด";
      if (to.diff(from, "days") > MAX_RANGE_DAYS)
        return "ช่วงวันที่ต้องไม่เกิน 93 วัน";
      return "";
    },
    getReport() {
      this.errorMessage = this.validateFilter();
      if (this.errorMessage) return;

      this.isBusy = true;
      this.failedPage = 1;
      getWhatsappDelivery(this.$chatBaseUrl, this.$headersChat, {
        from: moment(this.filter.from).format("YYYY-MM-DD"),
        to: moment(this.filter.to).format("YYYY-MM-DD"),
        brandId: this.brandID || this.$route.query.brandID
      })
        .then(response => {
          if (response.data.result == 1) {
            this.items = response.data.detail || [];
          } else {
            this.items = [];
            this.errorMessage = response.data.message || "ดึงข้อมูลไม่สำเร็จ";
          }
        })
        .catch(error => {
          this.items = [];
          this.errorMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            "ดึงข้อมูลไม่สำเร็จ";
        })
        .finally(() => {
          this.isBusy = false;
        });
    }
  },
  created: async function() {
    await this.$store.dispatch("getChatAdminProfile");
    this.$store.commit("setUsername", this.$cookies.get("d-order-username"));

    // รายงานสำหรับหัวหน้างานเท่านั้น
    if (!canViewWhatsappReport(this.adminProfile)) {
      this.$router.push("/AccessDenied");
      return;
    }
    this.getReport();
  }
};
</script>

<style scoped>
.report-page {
  background-color: #f7f7f9;
  position: absolute;
  width: 100%;
  height: calc(100vh - 51px);
  overflow-y: auto;
}

.report-container {
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
}

.report-card,
.stat-tile {
  background-color: white;
  border: 1px solid #e9e9ef;
  border-radius: 8px;
  padding: 12px 16px;
}

.stat-tile {
  height: 100%;
}

.stat-label {
  color: #6c757d;
  font-size: 13px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #212529;
}

.stat-note {
  color: #6c757d;
  font-size: 12px;
}

.legend {
  font-size: 13px;
  color: #495057;
}

.legend-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 6px;
}

.bucket-row {
  padding: 4px 0;
}

.bucket-date {
  width: 64px;
  flex-shrink: 0;
  font-size: 13px;
  color: #495057;
}

.bucket-bar {
  display: flex;
  flex-grow: 1;
  gap: 2px;
  height: 14px;
  min-width: 0;
}

.bucket-segment {
  min-width: 2px;
  height: 100%;
  cursor: default;
}

.bucket-segment:first-child {
  border-radius: 4px 0 0 4px;
}

.bucket-segment:last-child {
  border-radius: 0 4px 4px 0;
}

.bucket-segment:only-child {
  border-radius: 4px;
}

.bucket-count {
  width: 64px;
  flex-shrink: 0;
  text-align: right;
  font-size: 13px;
  color: #495057;
}
</style>
